/** @typedef {[string[], string]} AtlasId */
/**
 * @typedef {object} ObjContextItem
 * @property {string} psId - psId of the node
 * @property {string} type - type of the node
 * @property {string | string[]} id - ID if the node is a body, face, or edge
 * @property {string} [protoId] - the identifying id for the prototype
 */
/** @typedef {ObjContextItem[]} ObjContext */
/**
 *
 * @param {string[]} name
 * @param {string | string[]} id
 */
const pushAll = (name, id) => {
    if (Array.isArray(id)) {
        for (let mem of id) {
            name.push(mem);
        }
    } else {
        name.push(id);
    }
};

/**
 *
 * @param {AtlasId} name
 * @param {ObjContext} objContext
 */
const addProto = (name, objContext) => {
    let partIdx = 1;
    // Special case in assemblies... if the object in question is
    // the root component, type will be "Assembly" but length will still be 1.
    if (objContext[0].type === 'Part' || objContext.length === 1) {
        partIdx = 0;
    }
    if (objContext.length > partIdx) {
        // if this is a piece part, id is a string.
        // if this is an instance, id is an array, for which
        // last member is the part id.
        let id = objContext[partIdx].id;
        let partId = id;
        if (Array.isArray(id)) {
            partId = id[id.length - 1];
        }
        pushAll(name[0], partId);
        if (objContext[0].type !== 'Part') {
            //an object in a part
            name[1] = /** @type {string} */ (objContext[0].id);
        }
    }
};

/**
 *
 * @param {AtlasId} name
 * @param {ObjContext} objContext
 */
const addObject = (name, objContext) => {
    //if object === part just 0, otherwise just 0 and 1
    // Special case in assemblies... if the object in question is
    // the root component, type will be "Assembly" but length will still be 1.
    if ((objContext[0].type === 'Part' || objContext.length === 1) && doesIdExist(objContext[0])) {
        pushAll(name[0], objContext[0].id);
    } else if (objContext.length > 1) {
        if (doesIdExist(objContext[1])) {
            pushAll(name[0], objContext[1].id);
            name[1] = /** @type {string} */ (objContext[0].id);
        }
    }
};

/**
 *
 * @param {ObjContextItem} objContextItem
 * @returns {boolean}
 */
const doesIdExist = (objContextItem) => {
    if (Object.keys(objContextItem).length > 0) {
        if (objContextItem.hasOwnProperty('id')) {
            return true;
        }
    }
    throw Error(`id of psId ${objContextItem.psId} could not be found`);
};

/**
 * Given a psId, return its corresponding Atlas ID.
 *
 * @function fromPsId
 * @param {import('@pvw/plmvisweb').Viewer} viewer - a PLMVisWeb.Viewer object
 * @param {string} psId - input psId
 * @param {boolean} [partOnly] - true: return id in part context instead of assembly context
 * @returns {AtlasId} Atlas Id.
 */
export function fromPsId(viewer, psId, partOnly = false) {
    /** @type {ObjContext} */
    let objContext = viewer.getAncestryByPsId(psId, false);
    if (!objContext || objContext.length === 0) {
        throw Error('input psId could not be found in viewer');
    }
    /** @type {AtlasId} */
    let name = [[], ''];
    // if the object is a partition of a display entity, the display entity
    // appears as the second member of the array.  Atlas data model doesn't have this layer,
    // so we need to remove it
    if (objContext.length > 1 && objContext[1].type === 'DisplayEntity') {
        objContext.splice(1, 1);
    }

    //objContext will be ordered bottom up - object first, parent part/comp next, assy comp last
    // len 2: if part, occ in assy
    //        if not part, object in part
    // len 3: object in assy
    let isAssy = objContext[objContext.length - 1].type === 'Assembly';
    if (partOnly) {
        addProto(name, objContext);
    } else if (!isAssy) {
        addObject(name, objContext);
    } else {
        let partIdx = 1;
        // Special case in assemblies... if the object in question is
        // the root component, type will be "Assembly" but length will still be 1.
        if (objContext[0].type === 'Part' || objContext.length === 1) {
            partIdx = 0;
        }
        // add assembly id.  The first member of the psId of the assy is all that's needed
        if (objContext[partIdx + 1]) {
            if (doesIdExist(objContext[partIdx + 1])) {
                // Special case.  If the geometry involved is in the assembly part itself,
                // pushAll will duplicate the assembly id twice.  Id's in this case should
                // appear like any other part id.  Just skip it, and addObject will add the
                // part id.
                // In this case there are two unique things about the part's id:
                //   1) it's a string, not an array
                //   2) it's identical to the first member of the assembly id
                if (objContext[partIdx].id !== objContext[partIdx + 1].id[0]) {
                    pushAll(name[0], objContext[partIdx + 1].id[0]);
                }
            }
        }
        // else throw... There isn't any need for an error here.  This is a valid scenario where
        // items on the root component of the assembly or the root component itself are getting passed in.

        // then occ + obj:
        addObject(name, objContext);
    }
    return name;
}

/**
 * Given an atlas id, return its corresponding PLMVisWeb psId.
 *
 * @function toPsId
 * @param {string} plmVisWebModelId - an identifier assigned to this model in PLMVisWeb
 * @param {AtlasId} atlasId - a valid atlasId of any type
 * @returns {string} PLMVisWeb psId.
 */
export function toPsId(plmVisWebModelId, atlasId) {
    let psId = plmVisWebModelId + ':';
    if (Array.isArray(atlasId) && atlasId.length === 2) {
        let isAssembly = atlasId[0].length > 1;
        for (let i = isAssembly ? 1 : 0; i < atlasId[0].length; i++) {
            if (i === atlasId[0].length - 1) {
                if (atlasId[1].length > 0) {
                    psId += atlasId[0][i] + '|' + atlasId[1];
                } else {
                    psId += atlasId[0][i];
                }
            } else {
                psId += atlasId[0][i] + ',';
            }
        }
    }
    return psId;
}

/**
 * Extract context object ids from any valid Atlas id.
 *
 * @function extractIds
 * @param {AtlasId} atlasId - a valid atlasId of any type, in the context of an assembly or not.
 * @returns {{partId:string[], assemblyPartId?:string[], occurrenceId?:string[]}} An object with different members depending on the id passed in.
 *                   If in the context of an assembly, will return assemblyPartId, occurrenceId,
 *                   and partId.  Otherwise returns partId.
 */
export function extractIds(atlasId) {
    if (Array.isArray(atlasId) && Array.isArray(atlasId[0])) {
        if (atlasId[0].length === 1) {
            return { partId: atlasId[0] };
        }
        if (atlasId[0].length > 2) {
            let occId = [];
            for (let i = 0; i < atlasId[0].length - 1; i++) {
                occId.push(atlasId[0][i]);
            }
            return {
                assemblyPartId: [atlasId[0][0]],
                occurrenceId: occId,
                partId: [atlasId[0][atlasId[0].length - 1]]
            };
        }
    }
    throw new Error('Input is not a valid Atlas ID');
}

/**
 * Return true if input ids are the same values
 *
 * @function compareIds
 * @param {AtlasId} atlasId1 - a valid atlasId of any type, in the context of an assembly or not.
 * @param {AtlasId} atlasId2 - a valid atlasId of any type, in the context of an assembly or not.
 * @returns {boolean} true if ids are the same, false if not
 */
export function compareIds(atlasId1, atlasId2) {
    if (!Array.isArray(atlasId1) || !Array.isArray(atlasId2)) {
        return false;
    }
    if (atlasId1.length !== atlasId2.length) {
        return false;
    }
    if (atlasId1[1] !== atlasId2[1]) {
        return false;
    }
    if (!isSameContext(atlasId1, atlasId2)) {
        return false;
    }
    return true;
}

/**
 * Return true if input ids are from the same occurrence
 *
 * @function isSameContext
 * @param {AtlasId} atlasId1 - a valid atlasId of any type, in the context of an assembly or not.
 * @param {AtlasId} atlasId2 - a valid atlasId of any type, in the context of an assembly or not.
 * @returns {boolean} Returns true if the two atlas ids represent objects from the same occurrence
 *                    if in context of an assembly, or same part if not
 */
export function isSameContext(atlasId1, atlasId2) {
    if (!Array.isArray(atlasId1) || !Array.isArray(atlasId2)) {
        return false;
    }
    if (!Array.isArray(atlasId1[0]) || !Array.isArray(atlasId2[0])) {
        return false;
    }
    if (atlasId1[0].length !== atlasId2[0].length) {
        return false;
    }
    for (let i = 0; i < atlasId1[0].length; i++) {
        if (atlasId1[0][i] !== atlasId2[0][i]) {
            return false;
        }
    }
    return true;
}

/**
 * Return true if input ids are from the same prototype part
 *
 * @function isSamePrototype
 * @param {AtlasId} atlasId1 - a valid atlasId of any type, in the context of an assembly or not.
 * @param {AtlasId} atlasId2 - a valid atlasId of any type, in the context of an assembly or not.
 * @returns {boolean} Returns true if the two atlas ids represent objects from the same prototype
 *                    part, regardless if they are in the same occurrence or not
 */
export function isSamePrototype(atlasId1, atlasId2) {
    if (!Array.isArray(atlasId1) || !Array.isArray(atlasId2)) {
        return false;
    }
    if (!Array.isArray(atlasId1[0]) || !Array.isArray(atlasId2[0])) {
        return false;
    }
    if (atlasId1[0].length === 0 || atlasId2[0].length === 0) {
        return false;
    }
    if (atlasId1[0][atlasId1[0].length - 1] !== atlasId2[0][atlasId2[0].length - 1]) {
        return false;
    }
    return true;
}

/**
 * Return atlas id based on prototype of object, stripping any assembly/occurrence information
 *
 * @function toPrototype
 * @param {AtlasId} atlasId - a valid atlasId of any type, in the context of an assembly or not.
 * @returns {AtlasId | undefined} Returns atlas Id based on prototype part, stripping any assembly/occurrence information
 */
export function toPrototype(atlasId) {
    if (Array.isArray(atlasId) && Array.isArray(atlasId[0]) && atlasId[0].length > 0) {
        /** @type {AtlasId} */
        let ret = [...atlasId]; //shallow copy of array
        ret[0] = [atlasId[0][atlasId[0].length - 1]];
        return ret;
    }
    return undefined;
}

/**
 * Return atlas id relative to input occurrence
 *
 * @function toOccurrence
 * @param {AtlasId} atlasId - a valid atlasId based on a single part.
 * @param {string[]} occId - an occurrence id in the format output in ExtractIds
 * @returns {AtlasId | undefined} Returns atlas Id based on occurrence.  This function does not confirm the
 *                   prototype is actually the prototype of the input occurrence, the caller is
 *                   responsible for ensuring this is the case.
 */
export function toOccurrence(atlasId, occId) {
    if (Array.isArray(atlasId) && Array.isArray(atlasId[0]) && Array.isArray(occId)) {
        /** @type {AtlasId} */
        let ret = [...atlasId]; //shallow copy of array
        ret[0] = [...occId];
        ret[0] = ret[0].concat(atlasId[0]); //last member of context should be prototype
        return ret;
    }
}

/**
 * Return atlas id with item from atlasId and on occurrence of occurrenceId
 *
 * @function toFullAtlasId
 * @param {AtlasId} atlasId - a valid atlasId, either from an occurrence or prototype
 * @param {AtlasId} [occurrenceId] - a valid atlasId
 * @returns {AtlasId|undefined} Returns atlas Id based on occurrence.  This function will reject the input
 *                   if atlasId and occurrenceId don't have the same prototype
 */
export function toFullAtlasId(atlasId, occurrenceId) {
    if (!atlasId || !Array.isArray(atlasId)) {
        throw Error('atlasId must be valid');
    }
    if (!occurrenceId) {
        return toPrototype(atlasId);
    }
    if (!isSamePrototype(atlasId, occurrenceId)) {
        throw Error('input ids must have same prototype');
    }
    // @ts-ignore-next-line
    return toOccurrence(toPrototype(atlasId), extractIds(occurrenceId).occurrenceId);
}

/**
 * @param {AtlasId} atlasId - the id of any Atlas object
 * @returns {boolean} true if atlasId is for an occurrence object
 */
export function isOccurrenceId(atlasId) {
    // @ts-expect-error ts2345
    return Array.isArray(atlasId) && !isSameContext(atlasId, toPrototype(atlasId));
}

/**
 * Get the prototype part atlas id of an object
 *
 * @param {AtlasId} atlasId
 * @returns {AtlasId}
 */
export function getPrototypePartId(atlasId) {
    if (Array.isArray(atlasId) && Array.isArray(atlasId[0])) {
        return [[atlasId[0][atlasId[0].length - 1]], ''];
    }
    throw new Error('Input is not a valid Atlas ID');
}

/**
 * Get partOcc(SPLM.Assemblies.component) atlasId from an occurrence atlasId
 *
 * @param {AtlasId} atlasId
 * @returns {AtlasId | undefined}
 */
export function getPartOccId(atlasId) {
    if (isOccurrenceId(atlasId)) {
        return [[...atlasId[0]], ''];
    }
    return undefined;
}

export default {
    fromPsId,
    toPsId,
    extractIds,
    compareIds,
    isSameContext,
    isSamePrototype,
    toPrototype,
    toOccurrence,
    isOccurrenceId,
    toFullAtlasId,
    getPrototypePartId,
    getPartOccId
};
